import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { AdSupplierGroupUser, UserRole } from '@magnetar/admin-api-interfaces'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { adSupplierGroupUsersService } from '@/services/ad_supplier_group_users_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

export const columns: ColumnDef<AdSupplierGroupUser>[] = [
  {
    accessorKey: 'ad_supplier_group_user_id',
    header: 'ID',
    cell: ({ row }) => {
      const id: number = row.getValue('ad_supplier_group_user_id')
      return (
        <Badge variant="outline" className="font-medium">
          {id}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'user',
    header: '使用者',
    cell: ({ row }) => {
      const user = row.original.user
      return (
        <div>
          <div className="font-medium">{user?.user_name || '-'}</div>
          <div className="text-sm text-muted-foreground">{user?.account || '-'}</div>
        </div>
      )
    },
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => {
      return row.original.user?.email || '-'
    },
  },
  {
    accessorKey: 'user_role',
    header: '角色',
    cell: function RoleSelectCell({ row, table }) {
      const userRole = row.original.user_role
      const { token } = useAuth()
      const [isUpdating, setIsUpdating] = useState(false)
      const meta = table.options.meta as {
        reloadData?: () => void
        availableRoles?: UserRole[]
      }

      const handleRoleChange = async (roleId: string) => {
        if (!token) return
        setIsUpdating(true)
        try {
          const result = await adSupplierGroupUsersService.updateAdSupplierGroupUser(
            row.original.ad_supplier_group_user_id,
            { user_role_id: roleId === 'none' ? null : Number(roleId) },
            token,
          )
          if (result.success) {
            toast.success('角色更新成功')
            meta?.reloadData?.()
          } else {
            toast.error(result.error?.message || '角色更新失敗')
          }
        } catch {
          toast.error('角色更新失敗')
        } finally {
          setIsUpdating(false)
        }
      }

      return (
        <Select
          value={userRole?.user_role_id?.toString() || 'none'}
          onValueChange={handleRoleChange}
          disabled={isUpdating}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="選擇角色" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">無角色</SelectItem>
            {meta?.availableRoles?.map((role) => (
              <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                {role.role_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    },
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const user = row.original
      const { token } = useAuth()
      const [isDeleting, setIsDeleting] = useState(false)

      const handleDelete = async () => {
        if (!token) return
        setIsDeleting(true)
        try {
          const result = await adSupplierGroupUsersService.deleteAdSupplierGroupUser(
            user.ad_supplier_group_user_id,
            token,
          )
          if (result.success) {
            toast.success('使用者已從集團中移除')
            const meta = table.options.meta as { reloadData?: () => void }
            meta?.reloadData?.()
          } else {
            toast.error(result.error?.message || '移除使用者失敗')
          }
        } catch {
          toast.error('移除使用者失敗')
        } finally {
          setIsDeleting(false)
        }
      }

      return (
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isDeleting}>
                移除
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>確定要移除此使用者？</AlertDialogTitle>
                <AlertDialogDescription>
                  此操作會將「{user.user?.user_name}」從集團中移除，但不會刪除使用者帳號。
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>取消</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>確定移除</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )
    },
  },
]
